// Angular
import {
  Component,
  ComponentFactoryResolver,
  OnInit,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
// RxJS
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import moment from 'moment';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, first, map, takeUntil, tap } from 'rxjs/operators';
// NGRX
// Other Vendor

// State
import { AuthService, currentUser, LogOut, User, UserLoaded } from '@app/core/auth';
import { AppState } from '@app/core/reducers';
// Feature Flags

import { environment } from '@app/env-service';
import { AuthChangePasswordComponent } from '@app/modules/auth/components/auth-change-password/auth-change-password.component';
import { AuthFormModalComponent } from '@app/modules/auth/components/auth-form-modal/auth-form-modal.component';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
// App
import { TenantInfoService } from '@app/shared/services/tenant-info.service';
import Noti from '@app/shared/utils/Noti';
import { router } from '@uni/router/router';
import {
  AccountApiService,
  AccountModel,
  AccountStatus,
} from 'v2/modules/shared/services/api/account-api.service';

const { CANCELLED, DEMO, PAST_DUE, TRIAL } = AccountStatus;

const AccountStatusToSeverity = {
  [CANCELLED]: 'danger',
  [DEMO]: 'info',
  [PAST_DUE]: 'danger',
  [TRIAL]: 'warning',
};
const ACCOUNT_STATUSES_WITH_MESSAGE :string[] = [CANCELLED, DEMO, PAST_DUE, TRIAL];
const ACCOUNT_STATUSES_WITH_BADGE :string[] = [CANCELLED, PAST_DUE, TRIAL];
const ACCOUNT_STATUSES_WITH_TOAST :string[] = [CANCELLED, PAST_DUE];

/**
 * Provide the template a consistent interface of available properties via a
 * view model.  Use `_.foo` in the template to access a ViewModel property.
 */
class UserProfileComponentViewModel {
  account :AccountModel = null;
  companyName :string = environment?.clientConfig?.companyName ?? '';
  severity :string = 'default';
  showBadge :boolean = false;
  showMessage :boolean = false;
  status :(typeof ACCOUNT_STATUSES_WITH_MESSAGE)[number] | '' = '';
  user :any = null;
}

/**
 * This is not the profile itself, but the user menu accessible by clicking the
 * user's name in the top bar.
 */
@Component({
  selector: 'kt-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
})
export class UserProfileComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Public properties
  _$ :Observable<UserProfileComponentViewModel> = of({
    ...new UserProfileComponentViewModel(),
  });
  user$ :Observable<any>;
  account$ :Observable<AccountModel>;

  _notiTimer ?:number;

  AccountStatus = AccountStatus;

  /**
   * Component constructor
   *
   * @param store: Store<AppState>
   */
  constructor(
    private store :Store<AppState>,
    private router :Router,
    private componentFactoryResolver :ComponentFactoryResolver,
    private Feature :FeatureToggleService,
    private accountService :AccountApiService,
    private auth :AuthService,
    private modalSvc :NgbModal,
    private tenantInfoService :TenantInfoService,
  ) {}

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit() :void {
    this.buildViewModel();
  }

  ngAfterViewInit() {
    this.initStatusToast();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    if (this._notiTimer) {
      window.clearTimeout(this._notiTimer);
    }
  }

  goTo(route :string) {
    router.navigate(route);
  }

  openProfile() {
    this.user$.pipe(first()).subscribe((user) => {
      const modalRef = this.modalSvc.open(AuthFormModalComponent, {});
      modalRef.componentInstance.staff = user;
      modalRef.result.then(
        (user) => {
          this.auth.updateProfileModal(user);
        },
        (error) => {},
      );
    });
  }
  changePasswd() {
    const modalRef = this.modalSvc.open(AuthChangePasswordComponent, {});
  }

  updateProfile() {
    const modalRef = this.modalSvc.open(AuthFormModalComponent, {});
  }
  /**
   * Log out
   */
  logout() {
    this.store.dispatch(new LogOut());
    this.auth.logout();

    this.tenantInfoService.getTenantInfo().subscribe((tenantInfo) => {
      if (tenantInfo.enterprise_logout_url) {
        window.location.href = tenantInfo.enterprise_logout_url;

        return;
      }

      this.router.navigateByUrl('/auth/login');
      window.location.reload();
    });
  }

  buildViewModel() {
    this.account$ = this.accountService.get().pipe(catchError((err) => of(null)));
    this.user$ = this.store.pipe(select(currentUser));

    this._$ = combineLatest([this._$, this.account$, this.user$]).pipe(
      map(([_, account, user]) :UserProfileComponentViewModel => {
        const status = account?.luminous_account_status ?? '';
        let statusDependentProps = {};

        if (status !== _.status) {
          statusDependentProps = {
            // only update these if `status` has changed...
            severity: AccountStatusToSeverity[status] ?? 'default',
            showBadge: ACCOUNT_STATUSES_WITH_BADGE.includes(status),
            showMessage: ACCOUNT_STATUSES_WITH_MESSAGE.includes(status),
            status, // ...and also only update `status` if it has changed
          };
        }

        return {
          ..._,
          account,
          ...statusDependentProps,
          user,
        };
      }),
    );
  }

  initStatusToast() {
    this._$.pipe(takeUntil(this.destroy$)).subscribe(({ account, status }) => {
      switch (status) {
        case AccountStatus.CANCELLED: {
          const _stopProp = (e) => e.stopPropagation();
          Noti.error.fire({
            allowEnterKey: false,
            allowEscapeKey: false,
            allowOutsideClick: false,
            timer: 0,
            title: 'This account is no longer active.',
            onOpen(toast) {
              toast.addEventListener('click', _stopProp, true);
            },
            onClose(toast) {
              toast.removeEventListener('click', _stopProp);
            },
          });
          break;
        }
        case AccountStatus.PAST_DUE: {
          const date = moment(account.hyperline_last_invoice_date);

          if (date > moment().subtract(1, 'month')) return; // [calvin] we'll only be annoying if the balance is outstanding for > 1 month

          const _openUserMenu = (e) => {
            // @TODO [calvin] make this function open the user menu, but only
            // after we've already added action links to the error message
            // that allow the user to add their billing info.
          };
          const _fireNoti = () => {
            Noti.error.fire({
              timer: 0,
              title: 'Your account is past-due.',
              onOpen(toast) {
                toast.addEventListener('click', _openUserMenu, true);
              },
              onClose(toast) {
                toast.removeEventListener('click', _openUserMenu);
              },
            });
          };

          _fireNoti();

          this._notiTimer = window.setInterval(_fireNoti, 600_000); // 10 minutes = 10 * 60 (seconds) * 1000 (milliseconds)
          break;
        }
        default:
          return;
      }
    });
  }
}
